// Valor Tools inside Valor Web (Oct 15 2026). Ben: "Valor Tools is player-facing, Valor Web is not" — so every piece of this is
// switched on per player from Valor Tools' Admin page (Players › Valor Web). useVtOn(wid) is the one switch the screens read:
// false until Valor Tools says on, and false whenever it can't be reached, so nothing shows for anyone else.
import { useQuery } from "@tanstack/react-query";
import { api, type VtClaim, type VtMarks, type VtMe, type VtOpTarget } from "@/api/client";

export const TOOLS_URL = "https://valor-reforged-tools.com";
export const vtKeys = {
  me: (wid: number) => ["vt", "me", wid] as const,
  marks: (wid: number) => ["vt", "marks", wid] as const,
  card: (wid: number, name: string) => ["vt", "card", wid, name.toLowerCase()] as const,
};

export function useVtMe(wid: number | null | undefined) {
  return useQuery<VtMe>({ queryKey: vtKeys.me(wid || 0), queryFn: () => api.vt.me(wid as number), enabled: !!wid && wid > 0, staleTime: 5 * 60_000, retry: 0 });
}
/** Is Valor Tools switched on inside Valor Web for this player (on this world's account)? */
export function useVtOn(wid: number | null | undefined): boolean {
  return !!useVtMe(wid).data?.on;
}
/** The player's guilds' claims and op targets on this world (Valor Tools), refreshed every 2 minutes while on. */
export function useVtMarks(wid: number | null | undefined, enabled = true) {
  const on = useVtOn(wid);
  return useQuery<VtMarks>({ queryKey: vtKeys.marks(wid || 0), queryFn: () => api.vt.marks(wid as number), enabled: on && enabled && !!wid, staleTime: 60_000, refetchInterval: 120_000, retry: 0 });
}
export function useVtCard(wid: number | null | undefined, name: string | null | undefined) {
  const on = useVtOn(wid);
  return useQuery({ queryKey: vtKeys.card(wid || 0, name || ""), queryFn: () => api.vt.card(wid as number, name as string), enabled: on && !!wid && !!name, staleTime: 10 * 60_000, retry: 0 });
}

/** A Valor Tools page (a path on valor-reforged-tools.com). Switched on: through the Worker, which signs the player in there
 *  (worker/vt.ts /api/vt/go); otherwise the plain page. */
export function toolsHref(wid: number | null | undefined, to: string, on: boolean): string {
  return on && wid ? `/api/vt/go?w=${wid}&to=${encodeURIComponent(to)}` : TOOLS_URL + to;
}
export function openTools(wid: number | null | undefined, to: string, on: boolean): void {
  window.open(toolsHref(wid, to, on), "_blank", "noopener");
}

// ------------------------------------------------------------------ claims and op targets at a city (the map, the attack confirm)
export interface VtAt { claims: VtClaim[]; ops: VtOpTarget[] }
export function marksAt(m: VtMarks | undefined, x: number, y: number): VtAt {
  const claims = (m?.claims || []).filter((c) => c.x === x && c.y === y);
  const ops = (m?.ops || []).filter((o) => o.x === x && o.y === y);
  return { claims, ops };
}
const pad = (n: number) => String(n).padStart(2, "0");
const utc = (ms: number) => { const d = new Date(ms); return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`; };
/** The warning lines for attacking/supporting x,y: claimed by a guildmate (not you), and in a guild op. */
export function marksWarning(at: VtAt, attack: boolean): string[] {
  const out: string[] = [];
  const others = at.claims.filter((c) => !c.mine);
  if (attack && others.length) {
    const c = others[0];
    out.push(`Claimed by ${c.by}${c.tag ? ` [${c.tag}]` : ""}${c.until ? ` until ${utc(c.until)}` : ""}.`);
  }
  for (const o of at.ops.slice(0, 2)) out.push(`In guild op ${o.op}: ${o.mode === "support" ? "support" : o.mode === "timed" ? "timed capture" : "attack"} lands ${utc(o.land)}.`);
  return out;
}

/** "Paid · day pass until 14:00 UTC" and friends, for the Valor Tools card. */
export function planWords(p: VtMe["plan"], now = Date.now()): string {
  if (!p || !p.known) return "Not on Valor Tools' Dashboard for this world yet";
  const until = (ms: number) => { const left = ms - now; return left < 86400e3 ? `until ${utc(ms)}` : `for ${Math.ceil(left / 86400e3)} more days`; };
  const base = p.sub_until ? (p.sub_all ? `Monthly plan, every world ${until(p.sub_until)}` : `Monthly plan ${until(p.sub_until)}`)
    : p.day_until ? `Day pass ${until(p.day_until)}` : p.trial_until ? `Free trial ${until(p.trial_until)}` : p.paid ? "Paid" : "Free tier";
  const adds = [p.addons.battle ? "Battle reports" : "", p.addons.incoming ? "Incoming Intel" : ""].filter(Boolean);
  return adds.length ? `${base} · ${adds.join(" · ")}` : base;
}
